import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import type { DenominationItem } from '../utils/denominations';

type ApiErrorPayload = { success: false; message?: string; code?: string };

function asApiErrorPayload(e: unknown): ApiErrorPayload {
  if (axios.isAxiosError(e)) {
    const payload = e.response?.data as any;
    if (payload && typeof payload === 'object') {
      return {
        success: false,
        message: typeof payload.message === 'string' ? payload.message : e.message,
        code: typeof payload.code === 'string' ? payload.code : undefined,
      };
    }
    return { success: false, message: e.response?.status ? `Request failed (${e.response.status})` : e.message };
  }
  return { success: false, message: 'Request failed' };
}

function createCashierClient(): AxiosInstance {
  const c = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  c.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  c.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('cashier-auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return c;
}

const cashierClient = createCashierClient();

export type CashierShift = {
  id: string;
  status: string;
  shiftDate?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  actualStartAt?: string;
  actualEndAt?: string;
  openedAt?: string;
  closedAt?: string;
  openingCashBalance?: number;
  closingCashCounted?: number;
  expectedClosingCash?: number;
  discrepancyAmount?: number;
  discrepancyFlagged?: boolean;
  deviceId?: string | null;
};

export type CashierTransaction = {
  id: string;
  type?: string;
  amount?: number;
  playerId?: string;
  createdAt?: string;
  status?: string;
};

export type CashierCashDrop = {
  id: string;
  shiftId: string;
  totalAmount?: number;
  status?: string;
  createdAt?: string;
};

export async function cashierLogin(payload: {
  identifier: string;
  password: string;
  deviceFingerprint: string;
}): Promise<{ success: boolean; data?: { accessToken: string; cashier: any; shop: any }; message?: string }> {
  const { data } = await cashierClient.post('/cashier-auth/login', {
    username: payload.identifier,
    identifier: payload.identifier,
    password: payload.password,
    deviceFingerprint: payload.deviceFingerprint,
  });
  return data;
}

export async function fetchCashierMe(): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await cashierClient.get('/cashier/me');
  return data;
}

export async function cashierListAssignedShifts(): Promise<{ success: boolean; data?: CashierShift[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/shifts/assigned');
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierGetShift(
  shiftId: string
): Promise<{ success: boolean; data?: { shift: CashierShift; transactions?: CashierTransaction[]; cashDrops?: CashierCashDrop[] }; message?: string; code?: string }> {
  try {
    const { data } = await cashierClient.get(`/cashier/shifts/${encodeURIComponent(shiftId)}`);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierStartShift(
  shiftId: string,
  payload: { denominations: DenominationItem[]; deviceFingerprint?: string; deviceId?: string }
): Promise<{ success: boolean; data?: CashierShift; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(shiftId)}/start`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierCloseShiftBlind(
  shiftId: string,
  payload: { denominations: DenominationItem[] }
): Promise<{
  success: boolean;
  data?: {
    shiftId: string;
    actualClosingCash: number;
    expectedClosingCash: number;
    discrepancyAmount: number;
    discrepancyFlagged: boolean;
    status: string;
  };
  code?: string;
  message?: string;
}> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(shiftId)}/close`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierCreateCashDrop(payload: {
  shiftId: string;
  denominations: DenominationItem[];
  deviceId?: string;
}): Promise<{ success: boolean; data?: CashierCashDrop; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/cash-drops', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierListCashDrops(
  shiftId: string
): Promise<{ success: boolean; data?: CashierCashDrop[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/cash-drops', { params: { shiftId } });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierListTransactions(
  shiftId: string
): Promise<{ success: boolean; data?: CashierTransaction[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/transactions', { params: { shiftId } });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierInitiateHandover(
  fromShiftId: string,
  payload: { toShiftId: string; denominations: DenominationItem[] }
): Promise<{ success: boolean; data?: any; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(fromShiftId)}/handover`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierConfirmHandover(
  toShiftId: string,
  payload: { denominations: DenominationItem[] }
): Promise<{ success: boolean; data?: any; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(toShiftId)}/handover/confirm`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierRegisterDevice(payload: {
  deviceName?: string;
  deviceFingerprint: string;
  deviceType?: string;
  operatingSystem?: string;
  browserVersion?: string;
}): Promise<{ success: boolean; data?: { id: string }; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/devices/register', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

/** Register this browser as a cashier device (requires auth token in localStorage). */
export async function ensureCashierDeviceRegistered(): Promise<{
  ok: boolean;
  deviceId?: string;
  message?: string;
}> {
  const { getOrCreateDeviceFingerprint, getStoredDeviceId, setStoredDeviceId } = await import(
    '../utils/deviceFingerprint'
  );
  const existing = getStoredDeviceId();
  if (existing) return { ok: true, deviceId: existing };

  const deviceFingerprint = getOrCreateDeviceFingerprint();
  const reg = await cashierRegisterDevice({
    deviceFingerprint,
    deviceName:
      typeof navigator !== 'undefined'
        ? navigator.userAgent.slice(0, 120)
        : 'Cashier terminal',
    deviceType: 'web',
    operatingSystem: typeof navigator !== 'undefined' ? navigator.platform : undefined,
    browserVersion: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : undefined,
  });
  if (reg.success && reg.data?.id) {
    setStoredDeviceId(reg.data.id);
    return { ok: true, deviceId: reg.data.id };
  }
  return { ok: false, message: reg.message || 'Device registration failed' };
}

export async function cashierFetchPlayers(params?: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data?: any[]; pagination?: any }> {
  try {
    const { data } = await cashierClient.get('/cashier/players', { params });
    return data;
  } catch {
    return { success: false, data: [], pagination: undefined };
  }
}

export async function cashierCreatePlayer(payload: {
  phone?: string;
  email?: string;
  username?: string;
  password?: string;
}): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/players', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierDepositCredits(
  playerId: string,
  payload: { amount: number; payment_method: 'cash' | 'card' | 'bank transfer' }
): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/deposit`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierRedeemCredits(
  playerId: string,
  payload: { amount: number }
): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/redeem`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}
